<script lang="ts" setup>
import type { ContentFlashcard } from '~/models/Content/ContentFlashcards'
import type { BaseItem } from '~/models/Content/BaseItem'
import type { TTSLanguage } from '~/models/Brikke/TextToSpeech'
import { computed, ref, watch } from 'vue'
import { vIntersectionObserver } from '@vueuse/components'
import { faPause, faPlay } from '@fortawesome/pro-solid-svg-icons'
import { faArrowRight, faRotateLeft, faSpinner } from '@fortawesome/duotone-regular-svg-icons'
import { PendoTrackName } from '~/models/Pendo'
import { ContentType } from '~/models/Content/ContentType'
import { useUrlBuilder } from '~/composables/useUrlBuilder'
import usePendo from '~/composables/usePendo'
import useLanguageDetector from '~/composables/useLanguageDetector'
import { useFlashcardsQuery } from '~/composables/useFlashcardsQuery'
import FlashcardAnimation from '~/assets/lottie/Flashcard_Complete.json'
import RichTextRenderer from '~/components/utils/RichTextRenderer.vue'
import Button from '~/components/ui/Button.vue'
import AuSkeletonWrapper from '~/components/ui/AuSkeletonWrapper.vue'
import AuSkeleton from '~/components/ui/AuSkeleton.vue'
import TextToSpeechButton from '~/components/media/TextToSpeechButton.vue'
import Image from '~/components/media/Image.vue'
import Carousel from '~/components/media/Carousel.vue'
import AudioPlayer from '~/components/media/AudioPlayer.vue'
import FlashCard from '~/components/flashcards/FlashCard.vue'

const { locationId, isFullscreen = false } = defineProps<{
  locationId: number
  isFullscreen?: boolean
}>()

const { data, isLoading } = useFlashcardsQuery(locationId)
const { pendoTrack, pendoFlushNow } = usePendo()
const { buildAudioUrl } = useUrlBuilder()

const cards = ref<ContentFlashcard[]>([])
const isRevealed = ref(false)

const hasFlashcards = computed(() => data.value?.cards.some(card => card.bodyBack))
const mappedCards = computed(() => cards.value.map(card => ({
  card,
  image: (card.imageFront && data.value?.images && data.value.images.get(Number(card.imageFront.destinationContentId))!) || undefined,
  audio: (card.audioFront && data.value?.audioFiles && data.value.audioFiles.get(Number(card.audioFront.destinationContentId))!) || undefined,
})))

const detectLanguageFront = computed(() => mappedCards.value
  .filter(({ card }) => card.languageDetection && card.bodyFront)
  .map(({ card }) => ({
    contentId: card.contentId,
    text: [card.bodyFront ?? ''],
  }))
)

const detectLanguageBack = computed(() => mappedCards.value
  .filter(({ card }) => card.languageDetection && card.bodyBack)
  .map(({ card }) => ({
    contentId: card.contentId,
    text: [card.bodyBack ?? ''],
  }))
)

const { result: detectedLanguagesFront, isLoading: isDetectingLangFront } = useLanguageDetector(detectLanguageFront)
const { result: detectedLanguagesBack, isLoading: isDetectingLangBack } = useLanguageDetector(detectLanguageBack)

const getEffectiveTTSLanguage = (card: ContentFlashcard, side: 'front' | 'back'): TTSLanguage | null => {
  const detectedLanguages = side === 'front' ? detectedLanguagesFront : detectedLanguagesBack

  if (card.languageDetection && detectedLanguages.value.has(card.contentId)) {
    const detected = detectedLanguages.value.get(card.contentId)
    if (detected) {
      return detected
    }
  }

  return card.textToSpeechLanguage?.identifier || null
}

const sorting = computed(() => data.value?.location.sorting ?? (hasFlashcards.value ? 'shuffle' : 'priority'))
const fontSize = computed(() => data.value?.location.textSizes.heading)

function sortCards<T extends BaseItem>(cards: T[]): T[] {
  const newCards = [...cards]
  if (sorting.value === 'priority') {
    const priority = data?.value?.location.cards?.destinationContentIds ?? []
    return newCards.sort((a, b) => priority.indexOf(a.contentId) - priority.indexOf(b.contentId))
  }
  const random = Array.from({ length: newCards.length }, Math.random)
  return newCards.sort((a, b) => random[newCards.indexOf(a)] > random[newCards.indexOf(b)] ? 1 : -1)
}

function updateInViewEndObserver() {
  pendoTrack(PendoTrackName.FlashcardComplete, {
    title: data.value?.location.title ?? document.title,
    url: window.location.href
  })
  pendoFlushNow()
}

watch(data, (data) => {
  if (!data?.cards)
    return
  cards.value = sortCards(data.cards)
}, { immediate: true })
</script>

<template>
  <AuSkeletonWrapper
    v-if="isLoading"
    class="grid place-items-center"
    style="container-type: inline-size;"
  >
    <AuSkeleton
      height="50cqw"
      width="80cqw"
    />
  </AuSkeletonWrapper>
  <Carousel
    v-else
    :class="{
      'max-h-[1000px]': hasFlashcards && !isFullscreen,
      'max-h-[600px]': !hasFlashcards && !isFullscreen,
      'h-screen p-6': isFullscreen,
    }"
    :style="{
      '--carousel-spacing': isFullscreen ? '10rem' : '1rem',
      'container-type': 'inline-size',
    }"
    class="grid auto-rows-max"
    nav="below"
    data-ignore-tts
  >
    <template
      v-if="hasFlashcards"
      #pre="{ scrollTo }"
    >
      <div class="grid aspect-[3/2] h-[53.33cqw] max-h-full place-content-center place-items-center gap-5 text-4xl">
        <h1 class="text-5xl">
          {{ data?.location.title }}
        </h1>
        <p class="text-xl">
          {{ $t('flashcards.tip') }}
        </p>
        <Button
          class="bg-(--theme-20) hover:not-disabled:bg-(--theme-10)"
          color="white"
          size="xl"
          @click="scrollTo(1, { focus: true })"
        >
          {{ $t('flashcards.start') }}
          <font-awesome-icon :icon="faArrowRight" />
        </Button>
      </div>
    </template>

    <template #default="{ scrollTo }">
      <FlashCard
        v-for="{ card, image, audio } in mappedCards"
        :key="card.contentId"
        class="relative grid aspect-[3/2] h-[53.33cqw] max-h-full place-items-center overflow-hidden rounded-md bg-(--theme-20,white) text-(--theme-60,black) transition-opacity"
        :class="[fontSize, `flashcard-${card.locationId}`]"
        @reveal="isRevealed = $event"
      >
        <template #front>
          <Image
            v-if="image"
            :class="image.contentTypeIdentifier === ContentType.Svg ? 'object-contain' : 'object-cover'"
            :content="image"
            class="max-h-full min-h-0 !w-full min-w-0 place-self-stretch"
          />
          <div
            :class="{ 'sr-only': image }"
            class="max-w-[40ch] px-24"
            data-key-tts="front"
          >
            <RichTextRenderer :text="card.bodyFront ?? 'no text'" />
          </div>
        </template>

        <template
          v-if="card.bodyBack"
          #back
        >
          <div class="max-w-[40ch] px-24" data-key-tts="back">
            <RichTextRenderer :text="card.bodyBack" />
          </div>
        </template>

        <template
          v-if="audio"
          #overlay
        >
          <AudioPlayer
            :media-id="audio.metadata?.contentItemId"
            variant="big-button-no-text"
            :src="{
              src: buildAudioUrl(audio),
              type: audio.metadata?.mimeType ?? audio.file?.mimeType,
            }"
          />
        </template>
        <template v-else-if="card.textToSpeech && ((isRevealed && card.bodyBack) || (!isRevealed && card.bodyFront && !image))" #overlay>
          <TextToSpeechButton
            v-if="getEffectiveTTSLanguage(card, isRevealed ? 'back' : 'front')"
            :item="card"
            :language-code="getEffectiveTTSLanguage(card, isRevealed ? 'back' : 'front')!"
            :selector="`.flashcard-${card.locationId}`"
          >
            <template #trigger="{ activate, isPlaying, isActive, pause, generate, isLoadingTts }">
              <Button
                class="group grid size-32 -translate-x-1/2 cursor-pointer place-content-center rounded-full bg-(--theme-5) text-3xl text-(--theme-50) shadow-lg hover:not-disabled:text-[rgb(from_var(--theme-20)_r_g_b_/0.8)]"
                shape="square"
                size="lg"
                :disabled="isLoadingTts || isDetectingLangFront || isDetectingLangBack"
                @click="isPlaying ? pause() : isActive ? generate() : activate()"
              >
                <font-awesome-icon
                  :class="{ 'translate-x-[5%]': !isPlaying }"
                  :icon="isLoadingTts ? faSpinner : isPlaying ? faPause : faPlay"
                  size="xl"
                  :spin="isLoadingTts"
                />
              </Button>
            </template>
          </TextToSpeechButton>
        </template>
      </FlashCard>

      <div
        v-if="hasFlashcards"
        v-intersection-observer="[updateInViewEndObserver, { threshold: .5 }]"
        class="flex aspect-[3/2] h-[53.33cqw] max-h-full place-content-center place-items-center gap-5 text-4xl"
      >
        <LottieAnimation
          :animation-data="FlashcardAnimation"
          class="self-end"
          height="100%"
          width="250px"
        />
        <div class="flex flex-col items-center gap-8">
          <p class="text-center text-4xl">
            {{ $t('flashcards.complete') }}
          </p>
          <Button
            class="bg-(--theme-20) hover:not-disabled:bg-(--theme-10)"
            color="white"
            size="xl"
            @click="scrollTo(0, { focus: true }); cards = sortCards(data?.cards ?? [])"
          >
            <font-awesome-icon :icon="faRotateLeft" />
            {{ $t('flashcards.retry') }}
          </Button>
        </div>
        <LottieAnimation
          :animation-data="FlashcardAnimation"
          class="rotate-180 self-start"
          height="100%"
          width="250px"
        />
      </div>
    </template>

    <template #counter="{ index }">
      {{ Math.min(data?.cards.length ?? 0, Math.max(1, index + (hasFlashcards ? 0 : 1))) }} / {{ data?.cards.length }}
    </template>
  </Carousel>
</template>
